import { Injectable } from '@angular/core';

export interface TourActionHandle {
  close(): Promise<void> | void;
}

export type TourAction = () => Promise<TourActionHandle | void> | TourActionHandle | void;

export interface TourActionRegistrationWaitOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

const ACTION_REGISTRATION_WAIT_TIMEOUT_MS = 4000;

@Injectable({ providedIn: 'root' })
export class TourActionRegistryService {
  private readonly actions = new Map<string, TourAction>();
  private readonly handles = new Map<number, Map<string, TourActionHandle>>();
  private readonly registrationWaiters = new Map<string, Set<() => void>>();

  register(actionId: string, action: TourAction): () => void {
    this.actions.set(actionId, action);
    this.resolveRegistrationWaiters(actionId);
    return () => {
      if (this.actions.get(actionId) === action) {
        this.actions.delete(actionId);
      }
    };
  }

  has(actionId: string): boolean {
    return this.actions.has(actionId);
  }

  waitUntilRegistered(
    actionId: string,
    options: TourActionRegistrationWaitOptions = {}
  ): Promise<boolean> {
    if (this.has(actionId)) {
      return Promise.resolve(true);
    }
    if (options.signal?.aborted) {
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      let settled = false;
      let timeout: number | null = null;
      let onRegistered: () => void;
      let onAbort: () => void;
      const waiters = this.registrationWaiters.get(actionId) || new Set<() => void>();
      const settle = (registered: boolean) => {
        if (settled) {
          return;
        }
        settled = true;
        if (timeout !== null) {
          window.clearTimeout(timeout);
        }
        options.signal?.removeEventListener('abort', onAbort);
        waiters.delete(onRegistered);
        if (!waiters.size) {
          this.registrationWaiters.delete(actionId);
        }
        resolve(registered);
      };
      onAbort = () => settle(false);
      onRegistered = () => settle(true);

      waiters.add(onRegistered);
      this.registrationWaiters.set(actionId, waiters);
      options.signal?.addEventListener('abort', onAbort, { once: true });
      timeout = window.setTimeout(
        () => settle(false),
        options.timeoutMs ?? ACTION_REGISTRATION_WAIT_TIMEOUT_MS
      );
    });
  }

  async open(runId: number, actionId: string): Promise<TourActionHandle | null> {
    const current = this.handles.get(runId)?.get(actionId);
    if (current) {
      return current;
    }

    const action = this.actions.get(actionId);
    if (!action) {
      return null;
    }

    const handle = await action();
    if (!handle) {
      return null;
    }

    const runHandles = this.handles.get(runId) || new Map<string, TourActionHandle>();
    runHandles.set(actionId, handle);
    this.handles.set(runId, runHandles);
    return handle;
  }

  async close(runId: number, actionId: string): Promise<void> {
    const runHandles = this.handles.get(runId);
    const handle = runHandles?.get(actionId);
    if (!handle) {
      return;
    }

    runHandles?.delete(actionId);
    await handle.close();
    if (!runHandles?.size) {
      this.handles.delete(runId);
    }
  }

  async closeAll(runId: number): Promise<void> {
    const runHandles = this.handles.get(runId);
    if (!runHandles) {
      return;
    }

    this.handles.delete(runId);
    const failures: unknown[] = [];
    for (const handle of [...runHandles.values()].reverse()) {
      try {
        await handle.close();
      } catch (error) {
        failures.push(error);
      }
    }

    if (failures.length) {
      throw failures[0];
    }
  }

  private resolveRegistrationWaiters(actionId: string): void {
    const waiters = this.registrationWaiters.get(actionId);
    if (!waiters) {
      return;
    }
    for (const resolve of [...waiters]) {
      resolve();
    }
  }
}
